import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { dataApi, searchCustomerOrganisations, createCustomerOrganisation } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';

const formatCustomerName = (customer) =>
  `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email || 'Unnamed customer';

/**
 * LinkCustomerDialog — attach a customer (individual) or a business (customer
 * organisation) to a quote request.
 *
 * Filling in the details searches existing records at the same time: matches
 * surface in a dropdown to pick from, or dismiss to create a new one. The type
 * toggle switches between an individual (first/last name → customers) and a
 * business (single name → customer_organisations).
 *
 * onLinked(entity, type) — type is 'individual' or 'organisation'.
 * Set `individualOnly` to reuse this as a person picker (e.g. a driver), which
 * hides the toggle and only ever returns a customer.
 */
const LinkCustomerDialog = ({ isOpen, onClose, onLinked, individualOnly = false, title = 'Link Customer' }) => {
  const [type, setType] = useState('individual'); // 'individual' | 'business'
  const [form, setForm] = useState({ firstName: '', lastName: '', businessName: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchWrapRef = useRef(null);

  const isBusiness = !individualOnly && type === 'business';
  const searchTerm = (isBusiness ? form.businessName : `${form.firstName} ${form.lastName}`).trim();

  // Live lookup as details are entered — customers for individuals, organisations for businesses
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        let results;
        if (isBusiness) {
          try {
            results = await searchCustomerOrganisations(searchTerm);
          } catch {
            results = [];
          }
        } else {
          results = await dataApi.customerList({ search: searchTerm });
        }
        if (!cancelled) setSuggestions((results || []).slice(0, 6));
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchTerm, isBusiness]);

  useEffect(() => {
    const handler = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setShowSuggestions(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      if (isBusiness) {
        const name = form.businessName.trim();
        if (!name) { toast.error('Please enter the business name'); setSaving(false); return; }
        const org = await createCustomerOrganisation({
          name,
          roles: 'customer',
          phones: form.phone.trim() ? [form.phone.trim()] : [],
          emails: form.email.trim() ? [form.email.trim()] : [],
        });
        onLinked(org, 'organisation');
      } else {
        const firstName = form.firstName.trim();
        const lastName = form.lastName.trim();
        if (!firstName && !lastName) { toast.error("Please enter the customer's name"); setSaving(false); return; }
        const customer = await dataApi.customerCreate({
          firstName,
          lastName,
          phone: form.phone.trim(),
          email: form.email.trim(),
        });
        onLinked(customer, 'individual');
      }
    } catch (err) {
      console.error('Failed to create:', err);
      toast.error(err.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const suggestionsVisible = showSuggestions && suggestions.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Individual / Business toggle */}
          {!individualOnly && (
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40">
              {[['individual', 'Individual'], ['business', 'Business']].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setType(value); setSuggestions([]); }}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                    type === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Name fields — double as the search */}
          <div ref={searchWrapRef} className="relative">
            {isBusiness ? (
              <div className="space-y-1.5">
                <Label htmlFor="link-customer-business">Business name</Label>
                <Input
                  id="link-customer-business"
                  value={form.businessName}
                  onChange={(e) => setField('businessName', e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  disabled={saving}
                  autoComplete="off"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="link-customer-first">First name</Label>
                  <Input
                    id="link-customer-first"
                    value={form.firstName}
                    onChange={(e) => setField('firstName', e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="link-customer-last">Last name</Label>
                  <Input
                    id="link-customer-last"
                    value={form.lastName}
                    onChange={(e) => setField('lastName', e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Live match suggestions */}
            {suggestionsVisible && (
              <div className="absolute z-[70] left-0 right-0 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
                <div className="max-h-56 overflow-y-auto py-1">
                  {suggestions.map((entity) => (
                    <button
                      key={entity.$id}
                      type="button"
                      onClick={() => onLinked(entity, isBusiness ? 'organisation' : 'individual')}
                      className="w-full flex flex-col items-start px-3 py-2 text-left hover:bg-accent transition-colors"
                    >
                      <span className="text-sm font-medium">
                        {isBusiness ? (entity.name || 'Unnamed business') : formatCustomerName(entity)}
                      </span>
                      {!isBusiness && (entity.phone || entity.email) && (
                        <span className="text-xs text-muted-foreground">
                          {[entity.phone, entity.email].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {isBusiness && entity.tradingName && (
                        <span className="text-xs text-muted-foreground">{entity.tradingName}</span>
                      )}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowSuggestions(false)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium border-t hover:bg-accent transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  {isBusiness ? 'New business' : 'New customer'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="link-customer-phone">Phone</Label>
              <Input
                id="link-customer-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-customer-email">Email</Label>
              <Input
                id="link-customer-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? 'Adding…' : (isBusiness ? 'Add business' : 'Add customer')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkCustomerDialog;
